import React, { useState } from 'react';
import { Accordion, Box, Text, Badge, Group, Button, createStyles, Grid, Loader,  } from '@mantine/core';
import { IconCheck, IconDownload, IconPlus } from '@tabler/icons-react';
import { getStatusColor } from '../../../utils';

const useStyles = createStyles((theme) => ({
  accordionItem: {
    borderRadius: theme.radius.md,
    backgroundColor: theme.colors.gray[0],
  },
  headerGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  statusBadge: {
    borderRadius: "8px",
    padding: '4px 8px',
  },
  contentBox: {
    backgroundColor: "#F5FAFF",
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
    marginTop: theme.spacing.sm,
  },
  fileButtons: {
    display: 'flex',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.sm,
  },
}));

interface Props {
  batchReport?: any;
  rawData?: any;
  dateAndTime?: any;
  status?: any;
  heading?: any;
  hrData?: any;
  blueSubData: any;
}

const CustomAccordion: React.FC<Props> = ({ batchReport, rawData, dateAndTime, hrData, heading, status, blueSubData }) => {
  const { classes } = useStyles();
  const [currenTab, setCurrentTab] = useState<any>('raw');
  const [downloadState, setDownloadState] = useState<any>(null); // "downloading", "downloaded", or null


  // Function to handle download
  const handleDownload = () => {
    const dataToDownload = currenTab === 'raw' ? rawData : hrData;
    const fileName = dataToDownload?.name || 'file'; // Use exact name, assuming it includes the extension
    const fileContent = dataToDownload?.body || 'No content available';
  
    setDownloadState("downloading");

    const blob = new Blob([fileContent], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName; 
  
    document.body.appendChild(link); // Needed for Firefox browser
    link.click();
    document.body.removeChild(link); 
    URL.revokeObjectURL(link.href);
    setDownloadState("downloaded");
    setTimeout(() => setDownloadState(null), 3000)
  };
  
  

  return (
    <Accordion variant="separated" radius="md" chevron={<IconPlus size="1rem" />}>
      <Accordion.Item className={classes.accordionItem} value="interchange-acknowledgment">
        <Accordion.Control>
          <Grid className={classes.headerGroup} gutter={0}>
            <Grid.Col span={8}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center' }}>
                <Grid.Col span={5}>
                  <Text weight={500}>{heading}</Text>
                </Grid.Col>
                <Grid.Col span={5}>
                  <Text size="xs" color="dimmed">{rawData?.name}</Text>
                </Grid.Col>
              </Box>
            </Grid.Col>
            <Grid.Col span={2}>
              <Text size="xs" color="dimmed">{dateAndTime}</Text>
            </Grid.Col>
            <Grid.Col span={2}>
              <Badge color={getStatusColor(status)} className={classes.statusBadge}>
                <Text>{status}</Text>
              </Badge>
            </Grid.Col>
          </Grid>
        </Accordion.Control>
        <Accordion.Panel>
          <Box className={classes.contentBox}>
            <Text size="sm" color="#0570DE">{blueSubData}</Text>
            <Group className={classes.fileButtons}></Group>
          </Box>
          <Box mt={'16px'} sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", gap: "16px" }}>
              <Button variant={currenTab === 'raw' ? 'filled' : 'outline'} size="xs" onClick={() => setCurrentTab('raw')}>
                {rawData?.name}
              </Button>
              {hrData && (
                <Button variant={currenTab === 'humanReadable' ? 'filled' : 'outline'} size="xs" onClick={() => setCurrentTab('humanReadable')}>
                  {hrData?.name}
                </Button>
              )}
            </Box>
            <Box
      onClick={handleDownload}
      sx={{
        border: "1px solid #1B75BC",
        borderRadius: "8px",
        padding: "8px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        width: "40px", 
        height: "40px",
      }}
    >
      {downloadState === 'downloading' ? (
        <Loader
        color='blue'
        size={'md'}
        />
      ) : downloadState === 'downloaded' ? (
        <IconCheck color="#1B75BC"  />
      ) : (
        <IconDownload color="#1B75BC" size={16} />
      )}
    </Box>
          </Box>
          {currenTab === 'raw' && (
            <Box mt={'16px'} sx={{ borderRadius: "8px", padding: "10px", backgroundColor: "#F5F5F5" }}>
              <Text color='dimmed' style={{ overflow: "auto" }}>
                <pre>{rawData?.body}</pre>
              </Text>
            </Box>
          )}
          {currenTab === 'humanReadable' && (
            <Box mt={'16px'} sx={{ borderRadius: "8px", padding: "10px", backgroundColor: "#F5F5F5" }}>
              <Text color='dimmed'>
                <pre>{hrData?.body}</pre>
              </Text>
            </Box>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default CustomAccordion;
